import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CalendarDays, CreditCard, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/hooks/useMimis";
import { useEntitlement } from "@/hooks/useVideo";
import { BrandKit } from "@/components/account/BrandKit";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const day = (v) => (v ? new Date(v).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "—");

const statusOf = (s) => {
  if (s.stato === "canceled") return { label: "Disdetto", cls: "bg-[#F5F5F7] text-[#52525b]" };
  if (s.disdetta_programmata) return { label: "In disdetta", cls: "bg-amber-50 text-amber-700" };
  if (s.stato === "active" || s.stato === "trialing") return { label: "Attivo", cls: "bg-emerald-50 text-emerald-700" };
  return { label: "In verifica", cls: "bg-[#F5F5F7] text-[#52525b]" };
};

const Shell = ({ children }) => (
  <main className="min-h-[80vh] bg-[#F5F5F7] px-5 pb-24 pt-32 sm:px-8" data-testid="account-page">
    <div className="mx-auto max-w-2xl">
      <span className="eyebrow">Il tuo account</span>
      <h1 className="font-display mt-3 text-4xl font-bold tracking-tight sm:text-5xl">Il mio abbonamento</h1>
      <div className="mt-10">{children}</div>
    </div>
  </main>
);

const Info = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl bg-[#F5F5F7] p-5">
    <p className="flex items-center gap-2 text-sm text-[#52525b]"><Icon className="h-4 w-4 text-[#0066FF]" /> {label}</p>
    <p className="font-display mt-1.5 text-lg font-bold" data-testid={testId}>{value}</p>
  </div>
);

const CancelButton = ({ onConfirm, busy, until }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button disabled={busy} className="btn-ghost h-12 px-6 text-sm" data-testid="cancel-subscription-button">Disdici abbonamento</button>
    </AlertDialogTrigger>
    <AlertDialogContent className="w-[calc(100%-2rem)] rounded-[1.5rem]" data-testid="cancel-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display text-xl">Vuoi davvero disdire?</AlertDialogTitle>
        <AlertDialogDescription>
          Non ti addebiteremo più nulla. Mantieni l'accesso fino al {day(until)}.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-full" data-testid="cancel-dialog-keep">Resto abbonato</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="rounded-full bg-[#111111] hover:bg-black" data-testid="cancel-dialog-confirm">Sì, disdici</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const Plan = ({ sub, busy, onCancel, onResume }) => {
  const st = statusOf(sub);
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="card-soft p-7 hover:translate-y-0 sm:p-9" data-testid="subscription-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-display text-xl font-bold">MIMIS Video Italia</p>
        <span className={`rounded-full px-3 py-1.5 text-xs font-semibold ${st.cls}`} data-testid="subscription-status">{st.label}</span>
      </div>
      <p className="mt-6 flex items-end gap-2">
        <span className="font-display text-6xl font-extrabold tracking-tighter" data-testid="subscription-price">{sub.prezzo}€</span>
        <span className="pb-2 text-[#52525b]">al mese · piano {sub.nome_piano}</span>
      </p>
      <div className="mt-7 grid gap-3 sm:grid-cols-2">
        <Info icon={CalendarDays} label={sub.stato === "canceled" ? "Terminato" : sub.disdetta_programmata ? "Attivo fino al" : "Prossimo rinnovo"} value={day(sub.rinnovo)} testId="subscription-renewal" />
        <Info icon={CreditCard} label="Abbonato dal" value={day(sub.iniziato_il)} testId="subscription-start" />
      </div>
      {sub.disdetta_programmata && sub.stato !== "canceled" && (
        <p className="mt-6 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800" data-testid="subscription-cancel-notice">
          Hai disdetto: non ci saranno altri addebiti e l'accesso resta attivo fino al {day(sub.rinnovo)}.
        </p>
      )}
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        {sub.stato === "canceled" ? (
          <Link to="/tool/mimis-video#prezzo" className="btn-blu h-12 px-6 text-sm" data-testid="resubscribe-link">Abbonati di nuovo <ArrowRight className="arrow h-4 w-4" /></Link>
        ) : sub.disdetta_programmata ? (
          <button onClick={onResume} disabled={busy} className="btn-blu h-12 px-6 text-sm" data-testid="resume-subscription-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Riattiva abbonamento"}
          </button>
        ) : (
          <CancelButton onConfirm={onCancel} busy={busy} until={sub.rinnovo} />
        )}
      </div>
    </motion.div>
  );
};

const Empty = () => (
  <div className="card-soft p-9 text-center hover:translate-y-0" data-testid="no-subscription">
    <Sparkles className="mx-auto h-8 w-8 text-[#0066FF]" />
    <p className="font-display mt-4 text-2xl font-bold">Nessun abbonamento attivo</p>
    <p className="mt-2 text-[#52525b]">Starter a 9€ al mese con 10 video, oppure Pro a 19€ con video illimitati.</p>
    <Link to="/tool/mimis-video#prezzo" className="btn-blu mt-7 h-12 px-6 text-sm" data-testid="subscribe-link">Scopri il tool <ArrowRight className="arrow h-4 w-4" /></Link>
  </div>
);

export default function Account() {
  const { user, loading, login } = useAuth();
  const [sub, setSub] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const { ent } = useEntitlement();
  const { hash } = useLocation();
  usePageTitle("Il mio abbonamento | MIMIS", true);

  useEffect(() => {
    if (hash !== "#brand" || !ent) return undefined;
    const t = setTimeout(() => document.getElementById("brand")?.scrollIntoView({ behavior: "smooth" }), 300);
    return () => clearTimeout(t);
  }, [hash, ent]);

  useEffect(() => {
    if (loading || !user) return;
    api.get("/account/subscription")
      .then((r) => setSub(r?.data?.abbonamento ?? null))
      .catch((e) => { toast.error(errMsg(e)); setSub(null); });
  }, [user, loading]);

  const act = async (path, msg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/account/subscription/${path}`);
      setSub(data?.abbonamento ?? null);
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading || (user && sub === undefined))
    return <Shell><div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0066FF]" /></div></Shell>;
  if (!user)
    return (
      <Shell>
        <div className="card-soft p-9 text-center hover:translate-y-0" data-testid="account-login-required">
          <p className="font-display text-2xl font-bold">Accedi per vedere il tuo abbonamento</p>
          <button onClick={() => login("/abbonamento")} className="btn-blu mt-7 h-12 px-6 text-sm" data-testid="account-login-button">Accedi con Google</button>
        </div>
      </Shell>
    );
  return (
    <Shell>
      {sub ? <Plan sub={sub} busy={busy} onCancel={() => act("cancel", "Abbonamento disdetto. Nessun altro addebito.")} onResume={() => act("resume", "Abbonamento riattivato!")} /> : <Empty />}
      {ent && <BrandKit pro={!!ent.pro} />}
    </Shell>
  );
}
